import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { MessageSquare, CheckCircle2, XCircle, Wifi, WifiOff, Save, RefreshCw, ChevronDown, ChevronUp, ExternalLink, Zap, Settings2, Users, Hash } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminOkChat() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);

    // Form state
    const [baseUrl, setBaseUrl] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [saving, setSaving] = useState(false);

    // Test connection state
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    const [showCapabilities, setShowCapabilities] = useState(false);
    const [showEndpoints, setShowEndpoints] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/okchat/settings');
            setCfg(data);
            setBaseUrl(data.base_url || 'https://okchat.okgo.mx');
            setEnabled(data.enabled);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 401 || status === 403) {
                toast.error('Sesión expirada — vuelve a iniciar sesión');
            } else {
                toast.error('Error al cargar configuración OkChat');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { base_url: baseUrl, enabled };
            if (apiKey) payload.api_key = apiKey;
            await api.post('/admin/okchat/settings', payload);
            toast.success('Configuración guardada');
            setApiKey('');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const testConn = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post('/admin/okchat/test');
            setTestResult({ ok: true, ...data });
        } catch (e) {
            setTestResult({ ok: false, error: e?.response?.data?.detail || 'Sin respuesta' });
        } finally {
            setTesting(false);
        }
    };

    if (loading) return <div className="flex items-center justify-center h-48 text-muted-foreground">Cargando…</div>;

    const isConnected = cfg?.has_api_key;

    return (
        <div className="space-y-6 max-w-3xl">

            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-xl bg-[#0288D1] flex items-center justify-center">
                            <MessageSquare className="h-5 w-5 text-white" />
                        </div>
                        <h1 className="text-xl font-bold">OkChat — Chat en tiempo real</h1>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        Conecta OkChat como el sistema de mensajería principal de OkLoVendo.
                    </p>
                </div>
                <a
                    href="https://okchat.okgo.mx"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
                >
                    <ExternalLink className="h-3 w-3" /> okchat.okgo.mx
                </a>
            </div>

            {/* Status banner */}
            <div className={`rounded-lg border p-4 flex items-center gap-3 ${isConnected && cfg?.enabled ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-800'}`}>
                {isConnected && cfg?.enabled ? (
                    <><CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" /><div><p className="text-sm font-semibold text-green-700 dark:text-green-400">OkChat configurado</p><p className="text-xs text-green-600 dark:text-green-500">Clave API activa · {baseUrl}</p></div></>
                ) : (
                    <><WifiOff className="h-5 w-5 text-yellow-600 shrink-0" /><div><p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">Sin configurar</p><p className="text-xs text-yellow-600 dark:text-yellow-500">Ingresa la clave API de OkChat para activar el chat</p></div></>
                )}
            </div>

            {/* Settings card */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2 font-semibold text-sm">
                    <Settings2 className="h-4 w-4" /> Configuración de conexión
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">URL base de OkChat</label>
                        <input
                            value={baseUrl}
                            onChange={e => setBaseUrl(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            placeholder="https://okchat.okgo.mx"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                            Clave API {cfg?.has_api_key && <span className="text-green-600">(configurada: {cfg.api_key_masked})</span>}
                        </label>
                        <input
                            type="password"
                            value={apiKey}
                            onChange={e => setApiKey(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            placeholder={cfg?.has_api_key ? 'Escribe para cambiar la clave actual' : 'ok_...'}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="okchat-enabled"
                            checked={enabled}
                            onChange={e => setEnabled(e.target.checked)}
                            className="rounded"
                        />
                        <label htmlFor="okchat-enabled" className="text-sm">Chat OkChat habilitado en la plataforma</label>
                    </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                    <button
                        onClick={save}
                        disabled={saving}
                        className="flex items-center gap-1.5 bg-[#0288D1] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#0277BD] disabled:opacity-50"
                    >
                        <Save className="h-3.5 w-3.5" />
                        {saving ? 'Guardando…' : 'Guardar'}
                    </button>
                    <button
                        onClick={testConn}
                        disabled={testing || !cfg?.has_api_key}
                        className="flex items-center gap-1.5 border px-4 py-2 rounded-md text-sm font-medium hover:bg-secondary disabled:opacity-50"
                    >
                        {testing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Wifi className="h-3.5 w-3.5" />}
                        {testing ? 'Verificando…' : 'Probar conexión'}
                    </button>
                </div>

                {/* Test result */}
                {testResult && (
                    <div className={`rounded-md p-3 text-sm flex items-start gap-2 ${testResult.ok ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                        {testResult.ok
                            ? <><CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /><div><strong>Conexión exitosa</strong> · {testResult.latency_ms}ms · Estado: {testResult.status}</div></>
                            : <><XCircle className="h-4 w-4 shrink-0 mt-0.5" /><div><strong>Error:</strong> {testResult.error}</div></>
                        }
                    </div>
                )}
            </div>

            {/* Capabilities */}
            {cfg?.capabilities?.length > 0 && (
                <div className="rounded-lg border bg-card overflow-hidden">
                    <button
                        className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold hover:bg-secondary/50"
                        onClick={() => setShowCapabilities(v => !v)}
                    >
                        <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Capacidades de OkChat ({cfg.capabilities.length})</div>
                        {showCapabilities ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {showCapabilities && (
                        <div className="px-5 pb-4 flex flex-wrap gap-2">
                            {cfg.capabilities.map(c => (
                                <span key={c} className="text-xs bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 px-2 py-0.5 rounded-full font-mono">
                                    {c}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Endpoints */}
            {cfg?.endpoints?.length > 0 && (
                <div className="rounded-lg border bg-card overflow-hidden">
                    <button
                        className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold hover:bg-secondary/50"
                        onClick={() => setShowEndpoints(v => !v)}
                    >
                        <div className="flex items-center gap-2"><ExternalLink className="h-4 w-4 text-purple-500" /> Endpoints registrados ({cfg.endpoints.length})</div>
                        {showEndpoints ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {showEndpoints && (
                        <div className="px-5 pb-4 space-y-2">
                            {cfg.endpoints.map((ep, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs">
                                    <span className={`font-mono px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${ep.method === 'GET' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{ep.method}</span>
                                    <span className="font-mono text-muted-foreground">{ep.path}</span>
                                    <span className="text-muted-foreground hidden sm:inline">— {ep.description}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Sync info */}
            <div className="rounded-lg border bg-card p-5 space-y-3">
                <div className="flex items-center gap-2 font-semibold text-sm">
                    <Users className="h-4 w-4" /> Sincronización de usuarios
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-md border bg-muted/40 p-3">
                        <p className="text-xs font-semibold mb-1">OkLoVendo → OkChat</p>
                        <p className="text-xs text-muted-foreground">Cuando un usuario se registra en OkLoVendo, se crea automáticamente en OkChat via webhook <code className="bg-muted px-1 rounded">user.registered</code>.</p>
                    </div>
                    <div className="rounded-md border bg-muted/40 p-3">
                        <p className="text-xs font-semibold mb-1">OkChat → OkLoVendo</p>
                        <p className="text-xs text-muted-foreground">OkChat envía eventos via webhook al endpoint <code className="bg-muted px-1 rounded">POST /api/connect/webhook</code> de OkLoVendo para sincronizar usuarios de vuelta.</p>
                    </div>
                </div>
            </div>

            {/* Rooms info */}
            <div className="rounded-lg border bg-card p-5 space-y-3">
                <div className="flex items-center gap-2 font-semibold text-sm">
                    <Hash className="h-4 w-4" /> Canales de chat en OkLoVendo
                </div>
                <div className="space-y-2 text-xs text-muted-foreground">
                    <p>OkChat gestiona los siguientes tipos de salas para OkLoVendo:</p>
                    <ul className="list-disc list-inside space-y-1 ml-1">
                        <li><strong>Chat directo</strong> — Comprador ↔ Vendedor en un anuncio específico</li>
                        <li><strong>Soporte</strong> — Usuario ↔ Equipo de soporte de la plataforma</li>
                        <li><strong>Grupos</strong> — Comunidades o salas temáticas dentro del marketplace</li>
                    </ul>
                    <p className="mt-2">Los usuarios acceden al chat desde el botón <strong>"Chatear"</strong> en los anuncios y perfiles.</p>
                </div>
            </div>

            {/* Info box */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4 text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <p className="font-semibold">¿Cómo funciona OkChat como sistema de mensajería?</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs text-blue-600 dark:text-blue-400">
                    <li>Los usuarios de OkLoVendo pueden chatear con vendedores directamente desde los anuncios.</li>
                    <li>OkChat gestiona las salas, historial de mensajes y notificaciones push.</li>
                    <li>El widget de chat flotante aparece para todos los usuarios autenticados.</li>
                    <li>La integración reverse-link registra OkLoVendo en OkChat automáticamente al vincular.</li>
                </ul>
            </div>
        </div>
    );
}
